import { Injectable } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { DEVICE_PAIRING_METHOD_REQUIRED } from '../../../constants';
import {
  AddMfaBackup,
  AuthAccessTokenDetails,
  InitiateAuthentication,
  LoginOAuthParams,
  MfaEnrollment,
  SwapAuthForAccessTokens,
  UserRegistrationInfo
} from '../../../models';
import { LoginRememberMeUtilsService } from '../../login-remember-me/login-remember-me-utils-service/login-remember-me-utils.service';
import { ExistingUserMfaWrapperService } from '../existing-user/existing-user-mfa-wrapper.service';
import { NewUserMfaWrapperService } from './new-user-mfa-wrapper.service';

@Injectable({
  providedIn: 'root'
})
export class NewUserMfaEnrollmentService {
  retryCount = 0;

  constructor(
    private newUserMfaWrapper: NewUserMfaWrapperService,
    private existingUserMfaWrapper: ExistingUserMfaWrapperService,
    private loginRememberMeUtils: LoginRememberMeUtilsService
  ) {}

  initiateEnrollmentRegistration(
    loginOAuthParams: LoginOAuthParams,
    verifyEmailMfa: boolean,
    fieldValues: UserRegistrationInfo,
    password: string,
    skipMfa?: boolean
  ): Observable<MfaEnrollment | AuthAccessTokenDetails> {
    return this.newUserMfaWrapper
      .initiateUserRegistrationMfaWithEmail(loginOAuthParams, verifyEmailMfa)
      .pipe(
        switchMap(() =>
          this.newUserMfaWrapper.registerAccount(
            loginOAuthParams,
            password,
            fieldValues
          )
        ),
        switchMap(() => {
          return this.newUserMfaWrapper.emailHandler(
            loginOAuthParams,
            verifyEmailMfa
          );
        }),
        switchMap((res: AddMfaBackup | InitiateAuthentication | string) => {
          if (skipMfa && typeof res !== 'string') {
            return this.newUserMfaWrapper.finish(loginOAuthParams).pipe(
              switchMap((token: SwapAuthForAccessTokens) => {
                this.loginRememberMeUtils.sendTokenServiceWorker(
                  loginOAuthParams.oauthConfig,
                  token
                );
                const currentTime = new Date().getTime();
                const tokenDetails: AuthAccessTokenDetails = {
                  access_token: token.access_token ? token.access_token : '',
                  expires_in: token.expires_in,
                  token_issued_time: currentTime
                };
                return of(tokenDetails);
              })
            );
          } else {
            this.setApplicationIdAndName(res, loginOAuthParams);
            return this.mfaEnrollmentResponse(res);
          }
        }),
        catchError(() => {
          return timer(
            loginOAuthParams.oauthConfig.REGISTRATION_RETRY_TIMER
              ? loginOAuthParams.oauthConfig.REGISTRATION_RETRY_TIMER
              : 1000
          ).pipe(
            mergeMap(() => {
              this.retryCount = this.retryCount + 1;
              if (
                this.retryCount !==
                loginOAuthParams.oauthConfig.REGISTRATION_RETRY_COUNT
                  ? loginOAuthParams.oauthConfig.REGISTRATION_RETRY_COUNT
                  : 0
              ) {
                return this.initiateEnrollmentRegistration(
                  loginOAuthParams,
                  verifyEmailMfa,
                  fieldValues,
                  password
                );
              } else {
                this.retryCount = 0;
                return of({ flowId: '', load: false });
              }
            })
          );
        })
      );
  }

  mfaEnrollmentResponse(
    res: AddMfaBackup | InitiateAuthentication | string
  ): Observable<MfaEnrollment> {
    if (
      typeof res !== 'string' &&
      res.status === DEVICE_PAIRING_METHOD_REQUIRED
    ) {
      return of({ flowId: res.id ? res.id : '', load: true });
    } else {
      return throwError({ flowId: '', load: false });
    }
  }

  setApplicationIdAndName(
    res: AddMfaBackup | string,
    loginOAuthParams: LoginOAuthParams
  ) {
    if (typeof res !== 'string') {
      const applicationId =
        res.devicePairingMethods?.find(
          (device) =>
            device.deviceType === 'MOBILE' &&
            device.applicationName ===
              loginOAuthParams.oauthConfig.FLY_DELTA_POLLING_APP_NAME
        )?.applicationId ?? '';
      const applicationName =
        res.devicePairingMethods?.find(
          (device) =>
            device.deviceType === 'MOBILE' &&
            device.applicationName ===
              loginOAuthParams.oauthConfig.FLY_DELTA_POLLING_APP_NAME
        )?.applicationName ?? '';
      this.existingUserMfaWrapper.applicationId = applicationId;
      this.existingUserMfaWrapper.applicationName = applicationName;
      this.existingUserMfaWrapper.pkceCode = loginOAuthParams.pkceCode;
      this.existingUserMfaWrapper.enrollmentFlow = true;
    }
  }
}
